import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
  Button,
  useTheme,
  alpha,
  TableContainer,
  Paper,
} from '@mui/material';
import { BreakoutAllocation } from '../../api/protosCompiled/estimateCloseout/estimateCloseout_pb';
import { Breakout } from '../../api/protosCompiled/breakout/breakout_pb';
import { allocateCost, formatCurrency, safeDivide } from './closeoutHelpers';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';

type AllocationProps = {
  allocations: BreakoutAllocation.AsObject[];
  breakoutList: Breakout.AsObject[];
  costToAllocate: number;
  updateAllocation: Function;
  closeoutId: string;
  directLaborHours?: { [key: string]: number };
};

type InputCache = {
  allocationpercentage?: string;
  allocationexpense?: string;
};

export const Allocation = (props: AllocationProps) => {
  const theme = useTheme();

  // Common styles matching TotalPricingRow and TotalPricingHeader
  const rowStyle = {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.1)
          : alpha(theme.palette.common.black, 0.04),
    },
    '&:nth-of-type(even)': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.02)
          : alpha(theme.palette.common.black, 0.02),
    },
  };

  const cellStyle = {
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap' as const,
  };

  const inputCellStyle = {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  const headerCellStyle = {
    fontWeight: 600,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    padding: theme.spacing(1.5, 2),
    whiteSpace: 'nowrap' as const,
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const selectedChangeOrder = useStore(useUnityBuildStore).selectedChangeOrder;
  const estimateId = selectedEstimate?.estimateid ?? selectedChangeOrder?.changeorderid ?? '';
  const [allocations, setAllocations] = useState<BreakoutAllocation.AsObject[]>(
    props.allocations || []
  );
  // Local cache for user inputs (so we don't update computed values on every keystroke)
  const [inputCache, setInputCache] = useState<Record<string, InputCache>>({});

  // Helper: update the allocation for a given breakout and then update parent callback.
  const commitAllocationUpdate = (
    breakoutId: string,
    field: 'allocationpercentage' | 'allocationexpense',
    value: string
  ) => {
    const numericValue = parseFloat(value) || 0;
    setAllocations(prevAllocations => {
      const updatedAllocations = [...prevAllocations];
      const index = updatedAllocations.findIndex(alloc => alloc.breakoutid === breakoutId);
      let updatedAllocation: BreakoutAllocation.AsObject;

      if (index !== -1) {
        updatedAllocation = { ...updatedAllocations[index] };
      } else {
        // Find the existing allocation entry or the breakout entry if no allocation exists yet
        const existingAllocation = props.allocations.find(alloc => alloc.breakoutid === breakoutId);

        updatedAllocation = {
          id: existingAllocation?.id || '',
          estimateid: estimateId,
          closeoutid: props.closeoutId || '',
          breakoutid: breakoutId,
          allocationpercentage: 0,
          allocationexpense: 0,
        };

        updatedAllocations.push(updatedAllocation);
      }

      // Update the field and calculate the counterpart.
      if (field === 'allocationpercentage') {
        updatedAllocation.allocationpercentage = numericValue;
        updatedAllocation.allocationexpense = (numericValue / 100) * props.costToAllocate;
      } else if (field === 'allocationexpense') {
        updatedAllocation.allocationexpense = numericValue;
        updatedAllocation.allocationpercentage = (numericValue / props.costToAllocate) * 100;
      }

      updatedAllocations[index !== -1 ? index : updatedAllocations.length - 1] = updatedAllocation;

      // Call updateAllocation with the single updated allocation
      if (props.updateAllocation) {
        const sessionToken = '';
        const updatedAllocationWithToken = {
          ...updatedAllocation,
          sessiontoken: sessionToken,
        };
        props.updateAllocation(updatedAllocationWithToken);
      }
      return updatedAllocations;
    });
  };

  // When a field changes, update the local input cache.
  const handleInputChange = (
    breakoutId: string,
    field: 'allocationpercentage' | 'allocationexpense',
    value: string
  ) => {
    setInputCache(prev => ({
      ...prev,
      [breakoutId]: {
        ...prev[breakoutId],
        [field]: value,
      },
    }));
  };

  // When a field loses focus, commit the update.
  const handleBlur = (breakoutId: string, field: 'allocationpercentage' | 'allocationexpense') => {
    const value = inputCache[breakoutId]?.[field];
    if (value !== undefined) {
      commitAllocationUpdate(breakoutId, field, value);
      // Clear the cache for this field so that we fall back to the computed value.
      setInputCache(prev => ({
        ...prev,
        [breakoutId]: {
          ...prev[breakoutId],
          [field]: undefined,
        },
      }));
    }
  };

  // Retrieve the allocation for a given breakout id; if not present, return default values.
  const getAllocationForBreakout = (breakoutId: string) => {
    return (
      allocations.find(alloc => alloc.breakoutid === breakoutId) || {
        allocationpercentage: 0,
        allocationexpense: 0,
      }
    );
  };

  // Calculate total allocation percentage for validation
  const totalPercentage = allocations.reduce((sum, alloc) => sum + alloc.allocationpercentage, 0);

  // Allocate based on direct labor percentages
  const allocateByDirectLabor = () => {
    // Skip if no direct labor hours data
    if (!props.directLaborHours) {
      return;
    }

    // Calculate total direct labor hours
    const totalHours = Object.values(props.directLaborHours).reduce((sum, hours) => sum + hours, 0);
    if (totalHours <= 0) {
      return;
    }

    // Create new allocations based on labor percentages
    const updatedAllocations = props.breakoutList.map(breakout => {
      const breakoutId = breakout.id;
      const breakoutHours = props.directLaborHours?.[breakoutId] || 0;
      const percentage = safeDivide(breakoutHours, totalHours) * 100;
      const expense = (percentage / 100) * props.costToAllocate;

      // Find existing allocation or create new one
      const existingAlloc = allocations.find(a => a.breakoutid === breakoutId);

      return {
        id: existingAlloc?.id || '',
        estimateid: estimateId,
        closeoutid: props.closeoutId,
        breakoutid: breakoutId,
        allocationpercentage: percentage,
        allocationexpense: expense,
      };
    });

    // Update state
    setAllocations(updatedAllocations);

    // Update parent component with each allocation
    updatedAllocations.forEach(allocation => {
      if (props.updateAllocation) {
        props.updateAllocation({
          ...allocation,
          sessiontoken: '',
        });
      }
    });

    // Clear input cache
    setInputCache({});
  };

  // Reset all allocations to 0
  const resetAllocations = () => {
    const resetAllocations = props.breakoutList.map(breakout => ({
      id: allocations.find(a => a.breakoutid === breakout.id)?.id || '',
      estimateid: estimateId,
      closeoutid: props.closeoutId,
      breakoutid: breakout.id,
      allocationpercentage: 0,
      allocationexpense: 0,
    }));

    // Update state
    setAllocations(resetAllocations);

    // Update parent component with each allocation
    resetAllocations.forEach(allocation => {
      if (props.updateAllocation) {
        props.updateAllocation({
          ...allocation,
          sessiontoken: '',
        });
      }
    });

    // Clear input cache
    setInputCache({});
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: theme.palette.background.default,
          boxShadow: theme.shadows[1],
          borderRadius: 1,
          '& .MuiTable-root': {
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellStyle}>Breakout Name</TableCell>
              <TableCell sx={headerCellStyle}>Allocation %</TableCell>
              <TableCell sx={headerCellStyle}>
                Allocation {formatCurrency(props.costToAllocate)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.breakoutList.map((breakout, idx) => {
              const alloc = getAllocationForBreakout(breakout.id);
              return (
                <TableRow sx={rowStyle} key={idx}>
                  <TableCell sx={cellStyle}>{breakout.breakoutname || 'Unknown'}</TableCell>
                  <TableCell sx={inputCellStyle}>
                    <TextField
                      type="number"
                      value={
                        inputCache[breakout.id]?.allocationpercentage ??
                        alloc.allocationpercentage.toFixed(2)
                      }
                      onChange={e =>
                        handleInputChange(breakout.id, 'allocationpercentage', e.target.value)
                      }
                      onBlur={() => handleBlur(breakout.id, 'allocationpercentage')}
                      variant="outlined"
                      size="small"
                      inputProps={{
                        min: 0,
                        max: 100,
                        'aria-label': `Allocation percentage for ${breakout.breakoutname}`,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.palette.background.paper,
                          '&:hover': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.common.white, 0.05)
                                : alpha(theme.palette.common.black, 0.02),
                          },
                          '&.Mui-focused': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.common.white, 0.08)
                                : alpha(theme.palette.common.black, 0.05),
                          },
                          '& input': {
                            color: theme.palette.text.primary,
                          },
                          '& fieldset': {
                            borderColor: theme.palette.divider,
                          },
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={inputCellStyle}>
                    <TextField
                      type="text"
                      value={
                        inputCache[breakout.id]?.allocationexpense ??
                        formatCurrency(
                          allocateCost({
                            costToAllocate: props.costToAllocate,
                            allocationPrecentage: alloc.allocationpercentage,
                          })
                        )
                      }
                      onChange={e =>
                        handleInputChange(
                          breakout.id,
                          'allocationexpense',
                          e.target.value.replace(/[^0-9.]/g, '')
                        )
                      }
                      onBlur={() => handleBlur(breakout.id, 'allocationexpense')}
                      variant="outlined"
                      size="small"
                      inputProps={{
                        'aria-label': `Allocation amount for ${breakout.breakoutname}`,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.palette.background.paper,
                          '&:hover': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.common.white, 0.05)
                                : alpha(theme.palette.common.black, 0.02),
                          },
                          '&.Mui-focused': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.common.white, 0.08)
                                : alpha(theme.palette.common.black, 0.05),
                          },
                          '& input': {
                            color: theme.palette.text.primary,
                          },
                          '& fieldset': {
                            borderColor: theme.palette.divider,
                          },
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
          p: 2,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            flex: '0 0 200px',
            color: theme.palette.text.primary,
          }}
        >
          Allocation Remaining: {(100 - totalPercentage).toFixed(2)}%
        </Typography>

        {Math.abs(100 - totalPercentage) > 0.01 && (
          <Typography
            variant="caption"
            color="error"
            sx={{
              flex: '1',
              textAlign: 'center',
              color: theme.palette.error.main,
            }}
          >
            Total allocation must equal 100% of the cost.
          </Typography>
        )}

        <Box
          sx={{
            flex: '0 0 400px',
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
          }}
        >
          {props.directLaborHours && (
            <Button
              variant="contained"
              color="primary"
              onClick={allocateByDirectLabor}
              aria-label="Allocate by direct labor percentage"
            >
              Allocate by Direct Labor %
            </Button>
          )}
          <Button
            variant="outlined"
            color="secondary"
            onClick={resetAllocations}
            aria-label="Reset all allocations"
          >
            Reset
          </Button>
        </Box>
      </Box>
    </>
  );
};
